/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';
import { BehaviorSubject, firstValueFrom, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, switchMap } from 'rxjs/operators';

import { SpotlightKey } from '@reach/interfaces/spotlights/spotlights.enum';

import { SpotlightsApiService } from '~app-client/api/services';
import { ClientService, SessionService } from '~app-client/core/services';
import { InfoModalsService } from '~app-client/info-modals/services';
import {
	COMMON_NOT_WHEN_CONDITIONS,
	ONBOARDING_CONFIG_MAP,
	ONBOARDING_TO_INFO_MODAL_MAP,
} from '~app-client/onboarding/constants';
import {
	OnboardingCondition,
	OnboardingMessage,
	OnboardingStatus,
} from '~app-client/onboarding/types';
import { getConfigItem } from '~app-client/onboarding/utils';

@Injectable({
	providedIn: 'root',
})
export class OnboardingService {
	private readonly newValuesLoaded$$ = new Subject<void>();
	public readonly newValuesLoaded$ = this.newValuesLoaded$$.asObservable();

	private readonly pendingKeys$$ = new BehaviorSubject<SpotlightKey[]>([]);
	public readonly pendingKeys$ = this.pendingKeys$$.asObservable();

	private readonly needsToLoad$$ = new BehaviorSubject(true);
	private readonly needsToLoad$ = this.needsToLoad$$.asObservable();

	public readonly keysStatus$: Observable<Partial<Record<SpotlightKey, OnboardingStatus>>> =
		this.needsToLoad$.pipe(
			filter((l) => !l),
			switchMap(() => {
				return this.clientService.screen$;
			}),
			switchMap(() => {
				// check if previous keys are fulfilled.
				return this.pendingKeys$.pipe(
					map((keys) => {
						return keys.reduce(
							(acc, key) => {
								const included = keys.includes(key);

								if (!included) {
									acc[key] = OnboardingStatus.ALREADY_SEEN;
									return acc;
								}

								const config = ONBOARDING_CONFIG_MAP[key];

								if (!config) {
									console.warn(`No config for key "${key}"`);
									return acc;
								}

								const notBefore = getConfigItem(config.notBefore) || [];

								if (notBefore.length === 0) {
									acc[key] = OnboardingStatus.AVAILABLE;
									return acc;
								}

								const notBeforeArr = keys.filter((k) => {
									return notBefore.includes(k);
								});

								const status = (acc[key] =
									notBeforeArr.length === 0
										? OnboardingStatus.AVAILABLE
										: OnboardingStatus.WAITING_FOR_PREVIOUS_KEYS);

								acc[key] = status;
								return acc;
							},
							{} as Record<SpotlightKey, OnboardingStatus>
						);
					})
				);
			}),
			switchMap((statusMap) => {
				return this.activeConditions$.pipe(
					map((activeConditions) => {
						const newMap = { ...statusMap };

						Object.keys(statusMap).forEach((key: SpotlightKey) => {
							const status = statusMap[key];

							if (status === OnboardingStatus.AVAILABLE) {
								const config = ONBOARDING_CONFIG_MAP[key];
								const keyOnlyWhenConditions =
									getConfigItem(config.onlyWhenConditions) ?? [];
								const keyNotWhenConditions = [
									...(getConfigItem(config.notWhenConditions) ?? []),
									...COMMON_NOT_WHEN_CONDITIONS,
								].filter((k) => {
									return !keyOnlyWhenConditions.includes(k);
								});

								const validNotWhen =
									keyNotWhenConditions.filter((k) => {
										return activeConditions.includes(k);
									}).length === 0;
								const validWhen =
									keyOnlyWhenConditions.filter((k) => {
										return activeConditions.includes(k);
									}).length === keyOnlyWhenConditions.length;

								const newStatus =
									validWhen && validNotWhen
										? status
										: OnboardingStatus.WAITING_FOR_CONDITION;

								newMap[key] = newStatus;
							}
						});

						return newMap;
					})
				);
			}),
			distinctUntilChanged((curr, prev) => {
				try {
					return JSON.stringify(curr) === JSON.stringify(prev);
				} catch {
					return false;
				}
			}),
			shareReplay({ bufferSize: 1, refCount: true })
		);

	private loaded = false;
	private lastRequestId = 0;

	private readonly activeConditions$$ = new BehaviorSubject<OnboardingCondition[]>([]);
	private readonly activeConditions$ = this.activeConditions$$.asObservable();

	private readonly clientService = inject(ClientService);
	private readonly spotlightsApi = inject(SpotlightsApiService);
	private readonly sessionService = inject(SessionService);
	private readonly translocoService = inject(TranslocoService);
	private readonly infoModalService = inject(InfoModalsService);

	constructor() {
		this.sessionService.user$
			.pipe(
				distinctUntilChanged((prev, curr) => {
					return prev?.id === curr?.id && prev?.type === curr?.type;
				}),
				takeUntilDestroyed()
			)
			.subscribe((userId) => {
				this.clear();
				if (userId) {
					void this.load(true);
				}
			});
	}

	public keyStatus$(key: SpotlightKey): Observable<OnboardingStatus> {
		return this.keysStatus$.pipe(
			map((statusMap) => {
				return statusMap[key] || OnboardingStatus.ALREADY_SEEN;
			})
		);
	}

	public async waitUntilOnboardingStatus(
		key: SpotlightKey,
		statusToWaitFor: OnboardingStatus[]
	): Promise<OnboardingStatus> {
		const status = await firstValueFrom(
			this.keyStatus$(key).pipe(
				filter((iStatus) => {
					const canFinish = statusToWaitFor.includes(iStatus);
					return canFinish;
				})
			)
		);

		return status;
	}

	public async showOnboardingModal(key: SpotlightKey): Promise<boolean> {
		const infoModalKey = ONBOARDING_TO_INFO_MODAL_MAP[key];

		if (!infoModalKey) {
			throw new Error(`No info modal key for key "${key || 'no key provided'}"`);
		}

		const doneClicked = await this.infoModalService.openModal(infoModalKey, 1001);

		void this.markAsSeen(key);
		return doneClicked;
	}

	public async showOnboardingModalIfNotSeen(key: SpotlightKey): Promise<boolean> {
		await this.load();
		if (this.pendingKeys$$.value.includes(key)) {
			const doneClicked = await this.showOnboardingModal(key);
			return doneClicked;
		}
		return false;
	}

	public async getMessage(key: SpotlightKey): Promise<OnboardingMessage> {
		await firstValueFrom(
			this.translocoService.langChanges$.pipe(
				switchMap((lang) => {
					return this.translocoService.load(`onboarding/${lang}`);
				})
			)
		);

		const i18nKey = `onboarding.message.${key}`;
		const i18n: OnboardingMessage | string = await firstValueFrom(
			this.translocoService.selectTranslateObject(i18nKey)
		);

		if (typeof i18n !== typeof 'string' && !(i18n as OnboardingMessage).footer) {
			const footer = await firstValueFrom<string>(
				this.translocoService.selectTranslate('onboarding.common.message.footer')
			);

			(i18n as OnboardingMessage).footer = footer;
		}

		if (i18n === i18nKey) {
			throw new Error('can not find message');
		}

		return i18n as OnboardingMessage;
	}

	public async markAsSeen(key: SpotlightKey, force = false): Promise<void> {
		const status = force
			? OnboardingStatus.AVAILABLE
			: await firstValueFrom(this.keyStatus$(key));

		if ([OnboardingStatus.AVAILABLE, OnboardingStatus.WAITING_FOR_CONDITION].includes(status)) {
			this.pendingKeys$$.next(this.pendingKeys$$.value.filter((pk) => pk !== key));
			void this.spotlightsApi.markSpotlightsAsSeen([key]);
		}
	}

	public addCondition(condition: OnboardingCondition): void {
		const nextConditions = [...new Set([...this.activeConditions$$.value, condition])];
		this.activeConditions$$.next(nextConditions);
	}

	public removeCondition(condition: OnboardingCondition): void {
		const nextConditions = this.activeConditions$$.value.filter((k) => {
			return k !== condition;
		});
		this.activeConditions$$.next(nextConditions);
	}

	public async _markAllAsUnseenByUser(): Promise<void> {
		const newKeys = await this.spotlightsApi.markAllAsUnseenByUser();
		this.pendingKeys$$.next(newKeys);
		this.newValuesLoaded$$.next();
	}

	private async load(force = false): Promise<void> {
		if (!force && this.loaded) {
			return;
		}

		this.loaded = true;
		const requestId = this.getNextId();

		try {
			const keys = await this.spotlightsApi.getUnseenSpotlights();
			if (this.isLastRequestId(requestId)) {
				this.pendingKeys$$.next(keys);
				this.newValuesLoaded$$.next();
			}
		} catch (error) {
			console.error(error);
			this.loaded = false;
		} finally {
			if (this.isLastRequestId(requestId)) {
				this.needsToLoad$$.next(false);
			}
		}
	}

	private clear(): void {
		this.getNextId();
		this.loaded = false;
		this.needsToLoad$$.next(true);
		this.pendingKeys$$.next([]);
	}

	private getNextId(): number {
		this.lastRequestId += 1;
		return this.lastRequestId;
	}

	private isLastRequestId(id: number): boolean {
		return this.lastRequestId === id;
	}
}
